// The device layer of how the app looks (docs/plans/theme-layers.md): this device's own say on
// the theme and the brightness, over the person's and the workspace's. Each half is set or passes
// through. Per device, like the desk's settings: a phone in a dark room and a laptop in daylight
// want different things at the same time. localStorage `appearance-device`, `{v:2, theme?,
// brightness?, glass?}`. The old per-device mode (`appearance`) is read once (`deviceFromLegacy`)
// and left where it is.
import { create } from 'zustand'
import {
  deviceFromLegacy,
  patchLayer,
  resolveLayers,
  type LayerPatch,
  type LayerSetting,
  type Layers,
} from '@/lib/themeLayers'

const KEY = 'appearance-device'
const LEGACY = 'appearance'

const store = (layer: LayerSetting) => {
  try {
    localStorage.setItem(KEY, JSON.stringify({ v: 2, ...layer }))
  } catch {
    /* private mode: this session only */
  }
}

interface Start {
  layer: LayerSetting
  /** read from the old mode and not yet saved: the Shell decides whether Horizon must be pinned */
  pending: boolean
}

const read = (): Start => {
  try {
    const raw = localStorage.getItem(KEY)
    if (raw) {
      const layer = JSON.parse(raw) as LayerSetting & { v?: number }
      delete layer.v
      return { layer, pending: false }
    }
    const used = localStorage.getItem('settings-cache') !== null
    const layer = deviceFromLegacy(localStorage.getItem(LEGACY), used)
    // `workspace` (and a device with nothing to carry over) is no override: nothing to pin. The
    // decision is kept at once, or the next load would take the cache this one writes for a used device.
    if (layer.brightness === undefined) store(layer)
    return { layer, pending: layer.brightness !== undefined }
  } catch {
    return { layer: {}, pending: false }
  }
}

export const useDevice = create<{
  layer: LayerSetting
  pending: boolean
  /** change this device's layer; `null` clears a half */
  set: (patch: LayerPatch) => void
  clear: () => void
  /** the Shell's one decision on a migrated device: pin Horizon when the other layers would show a palette */
  settle: (pinHorizon: boolean) => void
}>((set, get) => ({
  ...read(),
  set: (patch) => {
    const layer = patchLayer(get().layer, patch)
    store(layer)
    set({ layer, pending: false })
  },
  clear: () => {
    store({})
    set({ layer: {}, pending: false })
  },
  settle: (pinHorizon) => {
    const layer = pinHorizon ? patchLayer(get().layer, { theme: { kind: 'horizon' } }) : get().layer
    store(layer)
    set({ layer, pending: false })
  },
}))

/** A device that was on Auto, Light, Dark, Black or Glass never showed a palette (it did show a saved theme): pin Horizon if the layers below would show one. */
export const needsHorizonPin = (below: Layers): boolean =>
  resolveLayers({ person: below.person, workspace: below.workspace }).theme.kind === 'palette'
